<!-- eslint-disable vue/html-closing-bracket-newline -->
<template>
  <section
    v-if="isStatementsConfigurationsDisplayed"
    class="statements-wrapper card px-6 py-5"
  >
    <div
      v-if="isSectionLoading"
      class="w-100 mx-auto"
      :style="{ height: isModifyingList ? '330.5px' : '176.5px', }"
      :class="{loader: isSectionLoading}"
    />
    <div
      v-else
    >
      <div
        v-if="GET_IS_MANAGED_ACCOUNT && GET_HAS_CAMPAIGN_ACCESS"
        class="manage-highlights-buttons-wrapper"
      >
        <button
          v-if="isModifyingList"
          class=" mr-3 btn btn-primary btn-sm"
          @click="updateHighlights()"
          :disabled="isSelectedStatementsOverLimit"
        >
          Done
        </button>
        <button
          class="btn btn-secondary btn-sm"
          @click="isModifyingList = true"
        >
          Manage Highlights
        </button>
      </div>
      <p
        v-if="isSelectedStatementsOverLimit"
        class="text-danger text-center"
      >
        You have exceeded the maximum number of {{ maxSelection }} highlights. Please, deselect {{ selectedStatementsList.length - maxSelection }} items to continue!
      </p>
      <div>
        <div
          v-for="(statement, index) in statementsList"
          :key="index"
          class="d-flex align-items-start highlight-statement"
        >
          <label
            v-if="isModifyingList"
            :id="`statement-switch-label-${statement.id}`"
            class="m-0 d-flex align-items-start pt-1"
            :style="{ cursor: isLastSelectedItem(statement) ? 'not-allowed' : 'pointer'}"
          >
            <input
              type="checkbox"
              :id="`statement-switch-input-${statement.id}`"
              class="custom-switch-input"
              :checked="statement.enabled"
              @change="toggleStatement(index, $event.target.checked)"
              :disabled="isLastSelectedItem(statement)"
            >
            <span class="custom-switch-indicator" />
          </label>
          <i
            class="fa-solid fa-diamond pt-1"
            :class="[isModifyingList ? 'mx-4' : 'mr-4', `${type}-icon-colour`]"
          />
          <p
            class="mb-0"
            :ref="`statement-value-${index}`"
          >
            <span
              v-html="getStatementValue(statement)"
              class="d-inline-block"
              @click="handleLineClick" />
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as accountTypes from '@/modules/account/store/types';

import _ from 'lodash';

export default {
  name: 'ReportingStatements',
  props: {
    type: {
      type: String,
      required: true,
      default: '',
      validator(value) {
        return ['CAMPAIGN_OVERVIEW', 'CAMPAIGN_PERFORMANCE', 'CONTENT_PERFORMANCE'].includes(value);
      },
    },
    maxSelection: {
      type: Number,
      default: 5,
    },
    hasReplacements: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isSectionLoading: true,
      isModifyingList: false,
      fullStatementsList: [],
      editReplacementConfigId: '',
      editReplacementKey: '',
      replacementsList: {},
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('reporting', [
      'reportingStatements',
      'campaignReportConfiguration',
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
    ]),
    isStatementsConfigurationsDisplayed() {
      if (this.isSectionLoading && !this.isModifyingList) return false;
      if (!this.GET_HAS_CAMPAIGN_ACCESS
        && this.campaignReportConfiguration.published === false) return false;
      return this.isSectionLoading === true
      || (this.isSectionLoading === false && this.fullStatementsList.length > 0);
    },
    selectedStatementsList() {
      if (!this.reportingStatements.hasOwnProperty([this.type])) return [];
      return this.fullStatementsList.filter((statement) => statement.enabled);
    },
    isSelectedStatementsOverLimit() {
      return this.selectedStatementsList.length > this.maxSelection;
    },
    statementsList() {
      if (this.type === '') return [];
      if (this.isModifyingList) return this.fullStatementsList;
      return this.selectedStatementsList;
    },
    hasChanged() {
      if (Object.keys(this.replacementsList).length) return true;
      if (!this.reportingStatements.hasOwnProperty([this.type])) return false;
      return JSON.stringify(this.fullStatementsList) !== JSON.stringify(this.reportingStatements[this.type]);
    },
  },
  methods: {
    ...mapActions('reporting', [
      reportingTypes.FETCH_REPORT_STATEMENTS_FOR_TYPE,
      reportingTypes.UPDATE_REPORT_STATEMENTS_FOR_TYPE,
      reportingTypes.FETCH_CAMPAIGN_REPORT_CONFIGURATION,
    ]),
    trimCurlyBrackets(keyword) {
      return keyword.replace('{{', '').replace('}}', '');
    },
    getReplacementKeyword(statement, stringToMatch) {
      if (this.replacementsList[statement.id]?.hasOwnProperty([`${this.trimCurlyBrackets(stringToMatch)}`])) {
        return this.replacementsList[statement.id][this.trimCurlyBrackets(stringToMatch)];
      }
      if (statement.replacements.hasOwnProperty(`${this.trimCurlyBrackets(stringToMatch)}`)) return statement.replacements[`${this.trimCurlyBrackets(stringToMatch)}`];
      return '';
    },
    isStatementWithReplacements(statement) {
      return statement.value.includes('{{') || statement.value.includes('}}');
    },
    isEditingReplacement(statement, stringToMatch) {
      return this.isModifyingList
          && this.editReplacementConfigId === statement.id.toString()
          && this.editReplacementKey === this.trimCurlyBrackets(stringToMatch);
    },
    getStatementValue(statement) {
      if (!this.isStatementWithReplacements(statement)) return statement.value;
      return this.replaceMacrosInStatement(statement, statement.value, 0);
    },
    replaceMacrosInStatement(statement, updatedStatement, searchStartPos) {
      const replacementStartPos = statement.value.indexOf('{{', searchStartPos);
      const replacementEndPos = statement.value.indexOf('}}', searchStartPos) + 2;
      if (replacementStartPos === -1 || replacementEndPos === -1) {
        return updatedStatement;
      }
      const stringToMatch = statement.value.slice(replacementStartPos, replacementEndPos);
      updatedStatement = updatedStatement.replace(stringToMatch, (this.isEditingReplacement(statement, stringToMatch) ? `${this.getMacrosEditInput(statement, stringToMatch)}` : `${this.getReplacementValue(statement, stringToMatch)}${this.getMacrosEditIcon(statement, stringToMatch)}`));
      const index = statement.value.indexOf(stringToMatch, searchStartPos);
      searchStartPos = index + stringToMatch.length;
      return this.replaceMacrosInStatement(statement, updatedStatement, searchStartPos);
    },
    getReplacementValue(statement, stringToMatch) {
      if (this.isModifyingList && this.getReplacementKeyword(statement, stringToMatch) !== '') {
        return `<b>[</b> ${this.getReplacementKeyword(statement, stringToMatch)} <b>]</b>`;
      }
      if (this.getReplacementKeyword(statement, stringToMatch) !== '') {
        return `${this.getReplacementKeyword(statement, stringToMatch)}`;
      }
      return '[-]';
    },
    getMacrosEditIcon(statement, stringToMatch) {
      return this.isModifyingList ? ` <b-button
              variant="transparant"
              id="btn-pencil-${statement.id}"
              data-key="${this.trimCurlyBrackets(stringToMatch)}"
              data-config-id="${statement.id}"
            >
              <i 
              id="btn-pencil-${statement.id}-icon"
              data-key="${this.trimCurlyBrackets(stringToMatch)}"
              data-config-id="${statement.id}"
              class="fas fa-edit"></i>
            </b-button>` : ' ';
    },
    getMacrosEditInput(statement, stringToMatch) {
      return ` <input
            id="edit-input-${statement.id}"
            class="d-inline-block"
            data-key="${this.trimCurlyBrackets(stringToMatch)}"
            data-config-id="${statement.id}"
            value="${this.getReplacementKeyword(statement, stringToMatch)}"
            autofocus
          />`;
    },
    closeEditInput() {
      this.editReplacementConfigId = '';
      this.editReplacementKey = '';
    },
    updateReplacementInStatementList(configId, replacementKey, replacementValue) {
      const statementIndex = this.fullStatementsList.findIndex((statement) => statement.id.toString() === configId);
      this.fullStatementsList[statementIndex].replacements[replacementKey] = replacementValue;
    },
    updateReplacement(e) {
      const { configId } = e.target.dataset;
      const replacementKey = e.target.dataset.key;
      const replacementValue = e.target.value;
      if (this.replacementsList.hasOwnProperty(`${configId}`)) {
        this.replacementsList[configId][replacementKey] = replacementValue;
        this.updateReplacementInStatementList(configId, replacementKey, replacementValue);
        this.closeEditInput();
        return;
      }
      this.replacementsList[configId] = {
        [replacementKey]: replacementValue,
      };
      this.closeEditInput();
    },
    populateReplacementsList(statement) {
      if (Object.keys(statement.replacements).length) this.replacementsList[statement.id] = statement.replacements;
    },
    handleLineClick(e) {
      if (e.target.id.includes('btn-pencil')) {
        this.editReplacementConfigId = e.target.dataset.configId;
        this.editReplacementKey = e.target.dataset.key;
      } else if (e.target.id.includes('edit-input')) {
        e.target.addEventListener('blur', this.updateReplacement, { once: true });
        e.target.addEventListener('keypress', (event) => {
          if (event.key === 'Enter') this.updateReplacement(event);
        });
      }
    },
    fillStatementsList(array) {
      this.fullStatementsList = _.cloneDeep(array);
    },
    toggleStatement(index, value) {
      this.fullStatementsList[index].enabled = value;
    },
    isLastSelectedItem(statement) {
      return statement.enabled && this.selectedStatementsList.length === 1;
    },
    updateHighlights() {
      if (this.hasChanged === false) {
        this.isModifyingList = false;
        return;
      }
      this.isSectionLoading = true;
      const enabledIds = this.selectedStatementsList.map((statement) => statement.id);
      this.UPDATE_REPORT_STATEMENTS_FOR_TYPE({
        type: this.type,
        enabledIds,
        fullStatementsList: this.fullStatementsList,
        replacements: this.replacementsList,
      })
        .catch(() => {
          this.fullStatementsList = [];
          this.fillStatementsList(this.reportingStatements[this.type]);
        })
        .finally(() => {
          this.isModifyingList = false;
          this.isSectionLoading = false;
        });
    },
  },
  async created() {
    await this.FETCH_CAMPAIGN_REPORT_CONFIGURATION();
    if (this.GET_IS_MANAGED_ACCOUNT === false
      && this.campaignReportConfiguration.published === false) return;
    this.FETCH_REPORT_STATEMENTS_FOR_TYPE(this.type)
      .then((response) => {
        this.fillStatementsList(response);
        for (const statement of response) {
          if (this.isStatementWithReplacements(statement)) this.populateReplacementsList(statement);
        }
        this.isSectionLoading = false;
      })
      .catch(() => {
        this.isSectionLoading = false;
      });
  },
};
</script>
<style lang="scss" scoped>
.statements-wrapper {
  position: relative;
}
.manage-highlights-buttons-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.custom-switch-indicator {
  min-width: unset;
  width: 29px;
  height: 13px;
  padding: 0 14px;
  &::before {
    width: calc(1rem - 5px);
    height: calc(1rem - 5px);
    top: 0px;
    left: 0px;
  }
}
.custom-switch-input:checked ~ .custom-switch-indicator:before {
  left: unset;
  right: 0px;
}
.highlight-statement:not(:nth-last-child(1)) {
  margin-bottom: 1rem;
}
</style>
